import { ContentCopy, Delete, Edit, OpenInNew } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'usehooks-ts'

import ConfirmationModal from '~/components/ConfirmationModal'
import DateUtils from '~/features/shared/utils/date.util'
import { type PlatformLanguage } from '~/utils/types/i18n'

import { useUpdateDynamicLink } from '../hooks/useDynamicLinks'
import { DynamicLinkState } from '../types/dynamic-links-states.enum'
import type { SurveyDynamicModel } from '../types/survey-dynamic-model.type'
import { urlFromSurvey } from '../utils/dynamic-link-generator'
import DynamicLinkEditModal from './DynamicLinkEditModal'

interface DynamicLinksSurveyItemProps {
  data: SurveyDynamicModel
  surveyDefinitionTitle?: string
  isLast?: boolean
}

export function DynamicLinksSurveyItem({
  data,
  surveyDefinitionTitle,
  isLast = false,
}: DynamicLinksSurveyItemProps) {
  const { t, i18n } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [, copyToClipboard] = useCopyToClipboard()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const updateMutation = useUpdateDynamicLink()

  const formattedDate = data.createdDate
    ? DateUtils.toShortDateFormat(
        data.createdDate,
        i18n.language as PlatformLanguage,
      )
    : ''

  const generatedUrl = urlFromSurvey(data)

  function handleOpenEditModal() {
    setIsEditModalOpen(true)
  }

  function handleCloseEditModal() {
    setIsEditModalOpen(false)
  }

  function handleOpenDeleteModal() {
    setIsDeleteModalOpen(true)
  }

  function handleCloseDeleteModal() {
    setIsDeleteModalOpen(false)
  }

  function handleConfirmDelete() {
    if (!data.id || !data.toDate) return

    updateMutation.mutate(
      {
        id: data.id,
        status: DynamicLinkState.EXPIRED,
        toDate: data.toDate,
      },
      {
        onSuccess: () => {
          handleCloseDeleteModal()
        },
      },
    )
  }

  async function handleCopyLink() {
    if (!generatedUrl) return
    try {
      await copyToClipboard(generatedUrl)
      enqueueSnackbar(t('general.copiedToClipboard'), {
        variant: 'success',
      })
    } catch (error) {
      enqueueSnackbar(t('dynamicLink.createLinkModal.copyToClipboardError'), {
        variant: 'warning',
      })
    }
  }

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase()
  }

  return (
    <>
      <ListItem divider={!isLast}>
        <ListItemAvatar>
          <Avatar>{data.createdBy ? getInitials(data.createdBy) : '?'}</Avatar>
        </ListItemAvatar>

        <ListItemText
          primary={
            <Stack gap={1} sx={{ ml: 2 }}>
              {' '}
              <Typography variant="body2" color="text.secondary">
                {surveyDefinitionTitle} • {formattedDate} •{' '}
                {t('views.dynamicLinks.createdBy')} {data.createdBy}
              </Typography>
              <Tooltip
                title={data.url ?? t('views.dynamicLinks.noUrl')}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    href={generatedUrl ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!generatedUrl}
                    endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                    sx={{ width: 'fit-content' }}
                  >
                    {t('views.dynamicLinks.linkLabel')}
                  </Button>
                </span>
              </Tooltip>
              {data.surveyCount !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {t('views.dynamicLinks.surveyCount', {
                    count: data.surveyCount,
                  })}
                </Typography>
              )}
            </Stack>
          }
        />

        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
          <Tooltip title={t('views.dynamicLinks.copyLink')} arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                disabled={!generatedUrl}
              >
                <ContentCopy sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('views.dynamicLinks.edit')} arrow>
            <IconButton size="small" onClick={handleOpenEditModal}>
              <Edit sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('views.dynamicLinks.delete')} arrow>
            <IconButton size="small" onClick={handleOpenDeleteModal}>
              <Delete sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </ListItem>

      <DynamicLinkEditModal
        survey={data}
        isOpenModal={isEditModalOpen}
        onCloseModal={handleCloseEditModal}
        surveyDefinitionTitle={surveyDefinitionTitle}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title={t('views.dynamicLinks.deleteConfirmTitle')}
        subtitle={t('views.dynamicLinks.deleteConfirmSubtitle')}
        confirmText={t('views.dynamicLinks.delete')}
        cancelText={t('views.dynamicLinks.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
        loading={updateMutation.isLoading}
      />
    </>
  )
}
